import compact from "lodash/compact";
import find from "lodash/find";
import pick from "lodash/pick";
import { KPIPill } from "PFApp/components/kpi_pill";
import { Button } from "PFComponents/button";
import { Inline } from "PFComponents/containers";
import { useKPIs } from "PFCore/hooks/queries/kpi";
import { Engagement } from "PFCore/types";
import { KPI, KPIStatus } from "PFCore/types/kpi";
import { useTranslation } from "react-i18next";
import { useToggle } from "react-use";

import css from "./kpi_cell.module.scss";

type KPIsCellProps = {
  KPIs: Engagement["kpis"];
};

type CellKPIData = Pick<KPI, "id" | "name"> & { status: KPIStatus };

const VISIBLE_KPI_STATUSES = [
  KPIStatus.ApprovalRequired,
  KPIStatus.ApprovalRequested,
  KPIStatus.ApprovalRejected
];

const DISPLAY_LIMIT = 3;

export const KPIsCell = ({ KPIs }: KPIsCellProps) => {
  const { t } = useTranslation("core");
  const [showAll, toggleShowAll] = useToggle(false);

  const { data: cellKPIsData } = useKPIs<CellKPIData[]>({
    select: (data) =>
      compact(
        KPIs.filter(({ status }) => VISIBLE_KPI_STATUSES.includes(status)).map(
          ({ indicator_id: indicatorId, status }) => {
            const KPIIndicator = pick(find(data.entries, ["id", indicatorId]), ["id", "name"]);

            return (
              KPIIndicator && {
                ...KPIIndicator,
                status
              }
            );
          }
        )
      ) as CellKPIData[]
  });

  const hasMoreItems = !!cellKPIsData && cellKPIsData.length > DISPLAY_LIMIT;
  const cellKPIsDataToShow = showAll ? cellKPIsData : cellKPIsData?.slice(0, DISPLAY_LIMIT);

  return (
    <Inline wrap="wrap" className={css.kpiCell}>
      {(cellKPIsDataToShow || []).map(({ id, name, status }) => (
        <KPIPill key={id} kpiStatus={status} kpiName={name} />
      ))}
      {hasMoreItems && (
        <Button kind="text" onClick={toggleShowAll}>
          {showAll ? t("showLess") : t("showNMore", { count: cellKPIsData.length - DISPLAY_LIMIT })}
        </Button>
      )}
    </Inline>
  );
};
